import {CellBuilderFlagIcon} from "/@back@/commons/widgets/grid-libs.js";
import {GridColDef, IGridDataRow} from "/@back@/commons/widgets/grid-core.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {EDrfState, ESrcField} from "/@lib@/wsp/src.js";
import {IShortDescCtx, ITEM} from "/@lib@/wsp/item.js";
import {SrcAction} from "/@back@/wsp/actions/srcActions.js";
import {POPUP, PopupTooltip} from "/@back@/commons/widgets/popups.js";
import {IWspUiEnv} from "/@lib@/wsp/wsp.js";
import {OUiCritInit, UiCritArea, UiCritBase} from "/@back@/wsp/widgets/search/uiCrit.js";
import {ButtonToggle} from "/@back@/commons/widgets/buttons.js";
import {ESearchExportColumnStatus, ISearchCrit, SearchDrfStates, SearchExportColumn, SearchFuncTranslateStr} from "/@lib@/wsp/search.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {TemplateResult} from "/@res@/litHtml/lib/template-result.js";
import {renderAppend, xhtml} from "/@lib@/commons/utils/htmlLit.js";
import {RibbonBase} from "/@back@/wsp/widgets/ribbon/ribbon.js";
import {IView} from "/@lib@/commons/views.js";
import {BarActions, OBarActionsInit} from "/@back@/commons/widgets/bars.js";
import {WSP_DRF} from "/@lib@/wsp/drf.js";
import {NavigatorArea} from "/@back@/wsp/areas/wspWorkbench.js";
import {NavigatorFacetText} from "/@back@/wsp/views/navigator.js";

export class CellBuilderDrfFlag extends CellBuilderFlagIcon {

	iconWidth = ".8rem";

	iconFilter = "var(--filter)";

	protected _buildContent(row: IGridDataRow, root: HTMLElement): HTMLElement {
		DOM.addClass(root, 'center');
		return super._buildContent(row, root);
	}

	protected _getIcon(row: IGridDataRow): string {
		const st = super._getValue(row);
		return st ? "--drf-" + st + "-url" : "";
	}

	protected _getValue(row: IGridDataRow): any {
		const st = super._getValue(row);
		return st ? WSP_DRF.getDrfStateLabel(st as EDrfState) : "";
	}
}

REG.reg.registerSkin("wsp-explorer/grid/drf", 1, /* language=CSS */ `
	:host {
		--drf-notOverriden-url: url(/@skin@/wsp/objects/drfState/notOverriden.svg);
		--drf-created-url: url(/@skin@/wsp/objects/drfState/created.svg);
		--drf-overriden-url: url(/@skin@/wsp/objects/drfState/overriden.svg);
		--drf-erased-url: url(/@skin@/wsp/objects/drfState/erased.svg);
	}

	.colW.drf {
		border: none;
	}
`);


/**
 *
 */
export class RevertSrc extends SrcAction<IShortDescCtx> {

	static SINGLETON = new RevertSrc();

	constructor() {
		super('revertSrc');
		this._group = "edit";
		this._icon = "/@skin@/wsp/actions/drfRevert.svg";
		this.atLeastOne = true;
		this.isNotRemoved = false;
		this._enableSrcPerms = ["action.drf#revert"];
	}

	getLabel(ctx: IShortDescCtx): string {
		if (ctx.shortDescs.length === 0) return "Abandonner toutes les modifications";
		if (ctx.shortDescs.every((sd) => sd.drfState === "erased")) return ctx.shortDescs.length === 1 ? "Annuler la suppression" : "Annuler les suppressions";
		return "Abandonner les modifications";
	}

	isEnabled(ctx: IShortDescCtx): boolean {
		if (ctx.shortDescs.some((sd) => {
			if (ITEM.getSrcUriType(sd.srcUri) !== "space") {
				const st = sd.drfState;
				return st !== "overriden" && st !== "erased" && st !== "notComputed";
			} else {
				return sd.drfState === "created";
			}
		})) return false;
		return super.isEnabled(ctx);
	}

	async execute(ctx: IShortDescCtx, ev?: Event) {
		const wsp = ctx.reg.env.wsp;
		if (await this.confirm(ctx, ev)) {
			await WSP_DRF.srcRevert(ctx.reg.env.wsp, ctx.emitter, ctx.shortDescs);
		}
	}

	confirm(ctx: IShortDescCtx, ev?: Event): Promise<boolean> {
		const count = ctx.shortDescs.length;
		if (count === 0) return POPUP.confirm("Confirmez l'abandon de toutes les modifications de cet atelier de travail. Le contenu original de l'atelier de référence sera restauré.", ctx.emitter, {okLbl: "Abandonner les modifications"});
		return POPUP.confirm(`Confirmez l'abandon de ces modifications. Le contenu original de l'atelier de référence sera restauré.`, ctx.emitter, {okLbl: "Abandonner les modifications"});
	}
}

REG.reg.registerSvc('drf/revertAction', 1, RevertSrc.SINGLETON);

/**
 *
 */
export class CommitSrc extends SrcAction<IShortDescCtx> {

	static SINGLETON = new CommitSrc();

	constructor() {
		super('commitSrc');
		this._group = "edit";
		this._icon = "/@skin@/wsp/actions/drfCommit.svg";
		this.atLeastOne = true;
		this.isNotRemoved = false;
		this._enableSrcPerms = ["action.drf#commit"];
	}

	getLabel(ctx: IShortDescCtx): string {
		if (ctx.shortDescs.length === 0) return "Valider toutes les modifications";
		if (ctx.shortDescs.every((sd) => sd.drfState === "erased")) return ctx.shortDescs.length === 1 ? "Valider la suppression" : "Valider les suppressions";
		return "Valider les modifications";
	}

	isEnabled(ctx: IShortDescCtx): boolean {
		if (ctx.shortDescs.some((sd) => {
			if (ITEM.isSrcUriInConflict(sd.srcUri)) return true;
			if (ITEM.getSrcUriType(sd.srcUri) !== "space") {
				const st = sd.drfState;
				return st !== "overriden" && st !== "created" && st !== "erased" && st !== "notComputed";
			} else {
				return false;
			}
		})) return false;
		return super.isEnabled(ctx);
	}

	async execute(ctx: IShortDescCtx, ev?: Event) {
		const wsp = ctx.reg.env.wsp;
		if (await this.confirm(ctx, ev)) {
			await WSP_DRF.srcCommit(ctx.reg.env.wsp, ctx.emitter, ctx.shortDescs);
		}
	}

	confirm(ctx: IShortDescCtx, ev?: Event): Promise<boolean> {
		const count = ctx.shortDescs.length;
		if (count === 0) return POPUP.confirm("Confirmez la validation de toutes les modifications de cet atelier de travail.", ctx.emitter, {okLbl: "Valider toutes les modifications"});
		if (count === 1) return POPUP.confirm("Confirmez la validation de cette modification.", ctx.emitter, {okLbl: "Valider la modification"});
		return POPUP.confirm(`Confirmez la validation de ces ${count} modifications.`, ctx.emitter, {okLbl: "Valider les modifications"});
	}
}

REG.reg.registerSvc('drf/commitAction', 1, CommitSrc.SINGLETON);

/**
 *
 */
export class RibbonDrf extends RibbonBase {

	protected _initialize(init: any): void {
		super._initialize(init);
		this.reg.env.longDescChange.add(this.refresh.bind(this));
		this.setTooltip(this._tooltip, true);
	}

	template(rib: RibbonDrf): TemplateResult {
		return xhtml`<div class="smallTi">Calque de travail</div><div id="st" data-drf-state="${rib.reg.env.longDesc.drfState}"><img id="ico"/>${WSP_DRF.getDrfStateLabel(rib.reg.env.longDesc.drfState)}</div>`
	}

	_tooltip(this: void, rib: RibbonDrf, tooltip: PopupTooltip): IView | null {
		return <div id="ttRoot">
			<BarActions class="vertical" î={{
				reg: rib.reg,
				actions: [rib.reg.getSvc('drf/revertAction'), rib.reg.getSvc('drf/commitAction')],
				actionContext: rib.shortDescCtx,
				uiContext: "dialog",
				disableFullOverlay: true
			} as OBarActionsInit<IShortDescCtx>}/>
		</div>;
	}

	protected _refresh() {
		if (!this.hidden) renderAppend(this.template(this), this.shadowRoot);
	}
}

REG.reg.registerSkin('wsp-rib-drf', 1, /* language=CSS */ `
	:host {
		flex: 0 1 auto;
		margin: 0 .2em;
		--drf-notOverriden-url: url(/@skin@/wsp/objects/drfState/notOverriden.svg);
		--drf-created-url: url(/@skin@/wsp/objects/drfState/created.svg);
		--drf-overriden-url: url(/@skin@/wsp/objects/drfState/overriden.svg);
		--drf-erased-url: url(/@skin@/wsp/objects/drfState/erased.svg);
	}

	#st {
		flex: 1 0 auto;
		color: var(--alt1-color);
		font-weight: bold;
	}

	#ico {
		width: 1em;
		height: 1em;
		margin-inline-end: 0.2em;
		filter: var(--filter);
	}

	#st[data-drf-state='notOverriden'] > #ico {
		content: var(--drf-notOverriden-url);
	}

	#st[data-drf-state='created'] > #ico {
		content: var(--drf-created-url);
	}

	#st[data-drf-state='overriden'] > #ico {
		content: var(--drf-overriden-url);
	}

	#st[data-drf-state='erased'] > #ico {
		content: var(--drf-erased-url);
	}
`);

customElements.define('wsp-rib-drf', RibbonDrf);


/**
 * Critère de recherche DRF : ItemCritDrfState
 *    <wsp-itemcrit-drf-state/>
 */
export class ItemCritDrfState extends UiCritBase {

	static AREA = new UiCritArea("wsp-itemcrit-drf-state").setLabel("État du calque de travail").setGroup("item")
		.setBuildAbstract((xml: Element, ctx: OUiCritInit): HTMLElement => {
			const statesCodesList = xml.hasAttribute("drfStates") ? xml.getAttribute("drfStates").split(" ") : [];
			const statesLabelsList: string[] = [];
			if (statesCodesList.indexOf("notOverriden") > -1) statesLabelsList.push("inchangé");
			if (statesCodesList.indexOf("overriden") > -1) statesLabelsList.push("modifié");
			if (statesCodesList.indexOf("created") > -1) statesLabelsList.push("créé");
			if (statesCodesList.indexOf("erased") > -1) statesLabelsList.push("supprimé");
			if (statesLabelsList.length > 0) {
				const statesTxt = statesLabelsList.join(" ou ");
				return <span class="crit">État du calque {statesTxt}</span>;
			}
		});


	protected _notOverridenBtn: ButtonToggle;

	protected _overridenBtn: ButtonToggle;

	protected _createdBtn: ButtonToggle;

	protected _erasedBtn: ButtonToggle;

	get notOverriden(): boolean {return this._notOverridenBtn.toggleOn}

	set notOverriden(state: boolean) {this._notOverridenBtn.toggleOn = state}

	get overriden(): boolean {return this._overridenBtn.toggleOn}

	set overriden(state: boolean) {this._overridenBtn.toggleOn = state}

	get created(): boolean {return this._createdBtn.toggleOn}

	set created(state: boolean) {this._createdBtn.toggleOn = state}

	get erased(): boolean {return this._erasedBtn.toggleOn}

	set erased(state: boolean) {this._erasedBtn.toggleOn = state}

	protected _initialize(init: OUiCritInit): void {
		super._initialize(init);
		const sr = this.shadowRoot;

		sr.appendChild(<div class="critHead">
			<span class="critTitle">État du calque de travail</span>
			{this.insertAlternativesBtn(init)}
		</div>);

		const btnsBox = sr.appendChild(<div class="btnsbox"/>);

		this._notOverridenBtn = btnsBox.appendChild(<ButtonToggle onclick={this.toggleButton} icon="/@skin@/wsp/objects/drfState/notOverriden.svg" label="Inchangés" class="notOverriden"
																															title="Items non surchargés dans cet atelier calque de travail" ui-context="dialog" aria-pressed="true"/>) as ButtonToggle;
		this._overridenBtn = btnsBox.appendChild(<ButtonToggle onclick={this.toggleButton} icon="/@skin@/wsp/objects/drfState/overriden.svg" label="Modifiés" class="icon overriden"
																													 title="Items surchargés dans cet atelier calque de travail" ui-context="dialog"/>) as ButtonToggle;
		this._createdBtn = btnsBox.appendChild(<ButtonToggle onclick={this.toggleButton} icon="/@skin@/wsp/objects/drfState/created.svg" label="Créés" class="created" title="Items créés dans cet atelier calque de travail"
																												 ui-context="dialog"/>) as ButtonToggle;
		this._erasedBtn = btnsBox.appendChild(<ButtonToggle onclick={this.toggleButton} icon="/@skin@/wsp/objects/drfState/erased.svg" label="Supprimés" class="erased" title="Items supprimés dans cet atelier calque de travail"
																												ui-context="dialog"/>) as ButtonToggle;
	}

	async initFromXmlCrit(xml: Element): Promise<this> {
		const states = xml.hasAttribute("drfStates") ? xml.getAttribute("drfStates").split(" ") : [];
		this.notOverriden = states.indexOf("notOverriden") > -1;
		this.overriden = states.indexOf("overriden") > -1;
		this.created = states.indexOf("created") > -1;
		this.erased = states.indexOf("erased") > -1;
		return this;
	}

	buildCrit(): ISearchCrit | undefined {
		const states: EDrfState[] = [];
		if (this.notOverriden) states.push("notOverriden");
		if (this.overriden) states.push("overriden");
		if (this.created) states.push("created");
		if (this.erased) states.push("erased");
		return new SearchDrfStates(states);
	}

	private toggleButton(this: ButtonToggle, ev?: Event): void {
		const me = DOMSH.findHost(this) as ItemCritDrfState;
		this.toggleOn = !this.toggleOn;
		me.onChange();
	}
}

REG.reg.registerSkin('wsp-itemcrit-drf-state', 1, /* language=CSS */ `
	.btnsbox {
		display: flex;
		flex-direction: row;
	}
`);

customElements.define("wsp-itemcrit-drf-state", ItemCritDrfState);

/**
 * Init du registre spécifique à un atelier de type DRF
 * 		init du registre **ui** (par app)
 */
export function initDrfReg(reg: IReg<IWspUiEnv>) {
	const drfColDef = new GridColDef("drf")
		.setFlex("1rem", 0, 0)
		.setCellBuilder(new CellBuilderDrfFlag("drfState"))
		.setSkin("wsp-explorer/grid/drf")
		.setLabel("")
		.setDescription("État du calque de travail");

	reg.addToList("wspApp:tools:rb", "drvChanges", 1, new NavigatorArea("drvChanges")
			.setLabel(() => "Items à valider")
			.setDescription("Liste des items à valider")
			.setIcon("/@skin@/wsp/views/navigator/workDrf.svg")
			.setEmptyTree((n) => <c-msg label="Aucun item à valider" level="info"/>)
			.setBuildFacets(async (nav, fields) => new NavigatorFacetItDrf(fields)
				.addCriterions([
					new SearchDrfStates(["overriden", "created", "erased"]).toDom()
				])
			)
			.setActionsLists(["actions:wspApp:navigator", "actions:wsp:shortDesc"])
			.setAccelKeysLists(["accelkeys:wspApp:navigator", "accelkeys:wsp:shortDesc"])
		, 10);

	reg.registerSvc("column.drf", 1, drfColDef);
	reg.addSvcToList("columns:wspExplorer:secondaryCols", 'drf', 1, "column.drf", 10);
	reg.addSvcToList("columns:netItems:secondaryCols", 'drf', 1, "column.drf", 10);

	reg.addSvcToList("columns:wspApp:searches:item", "drf", 1, "column.drf");

	reg.addToList("columns:wspApp:searches-export:item", "drf", 1, new SearchExportColumn<IRegPointer<IWspUiEnv>>('drf').setColDef(
		new SearchFuncTranslateStr("drfState").setDict(() => {
			const states: { [key in EDrfState]: string } = {
				erased: WSP_DRF.getDrfStateLabel("erased"),
				notOverriden: WSP_DRF.getDrfStateLabel("notOverriden"),
				overriden: WSP_DRF.getDrfStateLabel("overriden"),
				created: WSP_DRF.getDrfStateLabel("created"),
				createdDeleted: WSP_DRF.getDrfStateLabel("createdDeleted"),
				notComputed: WSP_DRF.getDrfStateLabel("notComputed"),
			};
			return states;
		})).setLabel("Calque de travail : état").setExportStatus(ESearchExportColumnStatus.exportable));

	function addSrcSvcAction(reg: IReg<IWspUiEnv>, svc: string, sortKey?: number) {
		reg.addSvcToList("actions:wsp:shortDesc", svc, 1, svc, sortKey);
		// reg.addToList("actions:wspApp:explorer", act.getId(), 1, act, sortKey);
		// reg.addToList("actions:item:burger", act.getId(), 1, act, sortKey);
		// reg.addToList("actions:searchItem:shortDesc:item", act.getId(), 1, act, sortKey);
	}

	addSrcSvcAction(reg, 'drf/revertAction', -2);
	addSrcSvcAction(reg, 'drf/commitAction', -1);

	reg.addToList("ribbon:item", "drf", 1, RibbonDrf, 20);

	REG.reg.registerSvc("wsp-itemcrit-drf-state", 1, ItemCritDrfState.AREA);
	reg.addSvcToList("wsp.crit.items", "wsp-itemcrit-drf-state", 1, "wsp-itemcrit-drf-state");
}

class NavigatorFacetItDrf extends NavigatorFacetText {
	constructor(fields: ESrcField[]) {
		super(fields);
		this.itemsExp = this.itemsExp.includeDrfErased(true);
	}

}